import { forceCollide, forceX, forceY } from "d3-force";
import { ChevronDown, FilePen, FileUp, FolderPlus, Globe2, Link2, Loader2, Maximize2, MessageCircleQuestion, NotebookPen, Orbit, SquarePlus, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import ForceGraph2D, { type ForceGraphMethods } from "react-force-graph-2d";
import { toast } from "sonner";

import { EmptyState, ErrorState, LoadingState, RestrictedState } from "@/components/feedback";
import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/overlays";
import { createMarkdownNote } from "@/lib/create-note";
import { keys, useClusterDetail, useExpand, useGraphOntology, useOverview, useWorkspace } from "@/hooks/queries";
import { useIsCompact } from "@/hooks/use-media-query";
import { useUpload } from "@/hooks/use-upload";
import { api, queryClient, type GraphOverview } from "@/lib/api";
import { authorableTypes } from "@/lib/ontology";
import { EDGE_COLOR, NODE_COLOR } from "@/lib/palette";
import { uploadAccept } from "@/lib/upload-accept";
import { cn } from "@/lib/utils";
import { useGraph, type GLink, type GNode } from "@/stores/graph";
import { useShell } from "@/stores/shell";
import { useTabs } from "@/stores/tabs";

/** Whether the atlas holds anything the user created. "Empty" must mean
 * EMPTY — not merely "no documents". Organizational hubs and saved
 * annotations (notes / AI outputs) keep the atlas alive after every document
 * is deleted, so the onboarding takeover must not treat that as a blank slate
 * and cover them. Sections/fragments are document-internal and concepts are
 * derived, so neither counts as user content here. */
function atlasHasContent(overview: GraphOverview): boolean {
  const c = overview.node_counts ?? {};
  return (c.document ?? 0) + (c.org ?? 0) + (c.note ?? 0) + (c.ai_output ?? 0) > 0;
}

import { OrgNodeDialog } from "./OrgNodeDialog";
import { GraphLayers } from "./GraphLayers";
import { NodeHoverCard, useHoverPoint } from "./NodeHoverCard";
import { edgeClass, linkColor, linkDash, linkText, linkWidth, nodeRadius, paintLinkLabel, paintNode } from "./paint";

/** Re-key the canvas when browser zoom changes devicePixelRatio so the chart
 * stays crisp instead of blurring. */
function useDevicePixelRatio(): number {
  const [dpr, setDpr] = useState(() => window.devicePixelRatio);
  useEffect(() => {
    const mq = window.matchMedia(`(resolution: ${dpr}dppx)`);
    const onChange = () => setDpr(window.devicePixelRatio);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [dpr]);
  return dpr;
}

function useContainerSize<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize({ width: Math.floor(width), height: Math.floor(height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, size };
}

function QuickItem({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] text-muted-foreground hover:bg-accent hover:text-foreground"
    >
      {icon}
      {label}
    </button>
  );
}

export function GraphTab({
  visible,
  onAddWebsite,
}: {
  visible: boolean;
  onAddWebsite: () => void;
}) {
  const overview = useOverview();
  const expand = useExpand();
  const clusterDetail = useClusterDetail();
  const upload = useUpload();
  const workspace = useWorkspace();
  const openReader = useTabs((s) => s.openReader);

  const pinnedIds = useMemo(
    () =>
      new Set(
        (workspace.data ?? [])
          .filter((i) => i.kind === "node" && i.ref_id)
          .map((i) => i.ref_id as string),
      ),
    [workspace.data],
  );

  const nodesMap = useGraph((s) => s.nodes);
  const linksMap = useGraph((s) => s.links);
  const version = useGraph((s) => s.version);
  const hiddenTypes = useGraph((s) => s.hiddenTypes);
  const hiddenEdgeTypes = useGraph((s) => s.hiddenEdgeTypes);
  const minConfidence = useGraph((s) => s.minConfidence);
  const selection = useGraph((s) => s.selection);
  const hoverNodeId = useGraph((s) => s.hoverNodeId);
  const flash = useGraph((s) => s.flash);
  const focusRequest = useGraph((s) => s.focusRequest);

  const fgRef = useRef<ForceGraphMethods<GNode, GLink>>();
  const { ref: boxRef, size } = useContainerSize<HTMLDivElement>();
  const dpr = useDevicePixelRatio();
  const didFitRef = useRef(false);
  const [expanding, setExpanding] = useState<string | null>(null);
  /** The one edge under the cursor. Only that edge may name itself — see
   *  paintLinkLabel. */
  const [hoverLinkId, setHoverLinkId] = useState<string | null>(null);
  /** The node the card is FOR, and where it goes.
   *
   * Separate from `hoverNodeId` and deliberately late. The halo dims on the
   * frame you enter a node, because that is a direct response to the pointer;
   * the card waits for you to stop. Sweeping the cursor across a cluster of
   * fifty nodes would otherwise open and discard fifty cards and fire fifty
   * requests for notes nobody looked at.
   *
   * The position is captured at the same moment, so the card does not then
   * chase the cursor around inside the node it is describing.
   */
  const [card, setCard] = useState<{ id: string; x: number; y: number } | null>(null);
  const pointer = useHoverPoint(boxRef);
  const cardTimer = useRef<number>(0);

  // --- canvas paint throttling (mobile battery/heat) ----------------------
  // Touch devices drop the per-frame flash rings past a node-count threshold;
  // the flash timestamp is fed live ONLY while a flash is animating; and the
  // whole simulation + render loop is paused while the tab is hidden (all tabs
  // stay mounted, so a backgrounded atlas would otherwise repaint forever).
  const coarsePointer = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches,
    [],
  );
  const [flashActive, setFlashActive] = useState(false);
  useEffect(() => {
    if (flash.size === 0) {
      setFlashActive(false);
      return;
    }
    const newest = Math.max(...flash.values());
    const remaining = 2700 - (Date.now() - newest); // flash window ~2.4s + slack
    if (remaining <= 0) {
      setFlashActive(false);
      return;
    }
    setFlashActive(true);
    const timer = window.setTimeout(() => setFlashActive(false), remaining);
    return () => window.clearTimeout(timer);
  }, [flash]);

  // Seed/refresh the atlas from the overview: documents + org hubs + concepts
  // + standalone notes/AI outputs + the visible edges between them (similar_to,
  // organizes, …). Sections and `contains` edges never appear — the atlas is
  // an idea graph.
  useEffect(() => {
    if (overview.data) {
      useGraph
        .getState()
        .mergeBatch(
          [
            ...overview.data.document_nodes,
            ...(overview.data.org_nodes ?? []),
            ...overview.data.concept_nodes,
            ...(overview.data.annotation_nodes ?? []),
          ],
          overview.data.edges,
        );
    }
  }, [overview.data]);

  // Hidden layers are dropped at the VIEW layer (the Layers panel), never from
  // the store: node objects keep their identity + simulation coords, so
  // toggling a category back on snaps it straight back into place rather than
  // re-running the simulation from scratch.
  //
  // Three filters, in order. A node type can be hidden; a relationship type can
  // be hidden; and an inferred link can fall below the confidence floor.
  // Deterministic links carry `confidence: null` and the floor never touches
  // them — a threshold on a thing with no confidence would just delete the
  // parser's structure at the first nudge of the slider. A link still shows
  // only when BOTH its endpoints are visible.
  const data = useMemo(() => {
    const nodes = [...nodesMap.values()].filter((n) => !hiddenTypes.has(n.type));
    const visible = new Set(nodes.map((n) => n.id));
    const links = [...linksMap.values()].filter((l) => {
      const s = typeof l.source === "object" ? l.source.id : l.source;
      const t = typeof l.target === "object" ? l.target.id : l.target;
      if (!visible.has(s as string) || !visible.has(t as string)) return false;
      if (hiddenEdgeTypes.has(l.edge.type)) return false;
      if (minConfidence > 0 && edgeClass(l) === "inferred") {
        return (l.edge.confidence ?? 0.35) >= minConfidence;
      }
      return true;
    });
    return { nodes, links };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, hiddenTypes, hiddenEdgeTypes, minConfidence]);

  // Physics: keep the atlas compact. Weaker repulsion + a gentle pull toward
  // the centre stop disconnected documents from drifting into distant orbits.
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    fg.d3Force("charge")?.strength(-58).distanceMax(320);
    fg.d3Force("link")
      // Pull similar documents close; org hubs gather their members tighter
      // still; concepts sit a little further as hubs.
      ?.distance((l: GLink) =>
        l.edge.type === "organizes"
          ? 26
          : l.edge.type === "similar_to"
            ? 30
            : l.edge.type === "mentions"
              ? 46
              : 56,
      )
      .strength((l: GLink) =>
        l.edge.type === "organizes" ? 0.85 : l.edge.type === "similar_to" ? 0.75 : 0.4,
      );
    fg.d3Force("collide", forceCollide<GNode>((n) => nodeRadius(n) + 4));
    // Centring forces gather otherwise-unconnected components near the middle.
    fg.d3Force("x", forceX<GNode>(0).strength(0.05));
    fg.d3Force("y", forceY<GNode>(0).strength(0.05));
  }, [version]);

  // The hovered node's adjacency set — drives Obsidian-style hover focus.
  const neighborIds = useMemo(() => {
    if (!hoverNodeId) return null;
    const set = new Set<string>([hoverNodeId]);
    for (const l of linksMap.values()) {
      const s = typeof l.source === "object" ? l.source.id : l.source;
      const t = typeof l.target === "object" ? l.target.id : l.target;
      if (s === hoverNodeId) set.add(t as string);
      else if (t === hoverNodeId) set.add(s as string);
    }
    return set;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoverNodeId, version]);

  const multiSelected = useGraph((s) => s.multiSelected);

  // --- edge drawing ("form edge") + rubber-band multi-select ---------------
  const [formEdge, setFormEdge] = useState(false);
  const [formSource, setFormSource] = useState<string | null>(null);
  /** A connection whose endpoints are chosen and whose MEANING is not. */
  const [edgeDraft, setEdgeDraft] = useState<{
    srcId: string;
    dstId: string;
    x: number;
    y: number;
  } | null>(null);
  const ontology = useGraphOntology();
  const [shiftHeld, setShiftHeld] = useState(false);
  const [band, setBand] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [orgDialog, setOrgDialog] = useState(false);
  // Concept-cluster chips (top-left): collapsible so a populated atlas isn't
  // buried under them. The manual toggle wins; until it's touched, default to
  // open on desktop and collapsed on compact/mobile where they'd eat the screen.
  const compact = useIsCompact();
  const [clustersManual, setClustersManual] = useState<boolean | null>(null);
  // Double-click on empty sky → quick-add menu at the cursor.
  const [quickAdd, setQuickAdd] = useState<{ x: number; y: number } | null>(null);
  const lastBgClick = useRef<{ t: number; x: number; y: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const deleteIntent = useGraph((s) => s.deleteIntent);

  // Reset the inline delete confirmation whenever the selection changes.
  useEffect(() => {
    setBulkConfirm(false);
  }, [multiSelected]);

  // The Del key opens the bulk confirm when there's a multi-selection.
  useEffect(() => {
    if (deleteIntent > 0 && useGraph.getState().multiSelected.size > 0) setBulkConfirm(true);
  }, [deleteIntent]);

  // The mobile add-sheet and the tools menu raise create intents. The graph tab
  // still owns the ORG hub dialog, which is a graph-only object; consume that
  // one here and leave the rest to the shell.
  const pendingCreate = useShell((s) => s.create);
  useEffect(() => {
    // "note" is handled by the shell, which mounts the chooser above every
    // surface — a tools-menu entry must work with the atlas panel closed.
    if (pendingCreate === "org") {
      setOrgDialog(true);
      useShell.getState().clearCreate();
    }
  }, [pendingCreate]);

  // Suppress d3-zoom's double-click-to-zoom: double-click is our quick-add
  // gesture (node double-click uses click timing, so it's unaffected).
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const stop = (e: MouseEvent) => e.stopPropagation();
    el.addEventListener("dblclick", stop, true);
    return () => el.removeEventListener("dblclick", stop, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track Shift so the rubber-band overlay only intercepts during shift-drag.
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "Shift") setShiftHeld(true);
      if (e.key === "Escape") {
        setFormEdge(false);
        setFormSource(null);
        setEdgeDraft(null);
        setQuickAdd(null);
        useGraph.getState().clearMultiSelected();
        // Clear the single selection too — but not while the user is typing in
        // a field (renaming a node, editing an edge label, writing a note),
        // where Escape means "cancel this edit," not "close the inspector."
        const el = e.target as HTMLElement | null;
        const typing =
          !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
        if (!typing) useGraph.getState().select(null);
      }
    };
    const up = (e: KeyboardEvent) => e.key === "Shift" && setShiftHeld(false);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  /** Draw a connection by hand.
   *
   * Every hand-drawn edge used to be a `similar_to`, whatever the person meant
   * by it — so "this contradicts that" and "this depends on that" were both
   * stored as a vague resemblance, and the graph lost the distinction the
   * moment it was made. The caller now names the relationship, chosen from the
   * types the SERVER says are legal for this pair. */
  const createUserEdge = useCallback(async (srcId: string, dstId: string, type: string) => {
    if (srcId === dstId) return;
    try {
      const edge = await api.request<import("@/lib/api").EdgeOut>("POST", "/api/edges", {
        src_id: srcId,
        dst_id: dstId,
        type,
      });
      useGraph.getState().mergeBatch([], [edge]);
      // A new connection invalidates the orbit layout — light the dirty banner.
      void queryClient.invalidateQueries({ queryKey: keys.orbits });
      toast.success("Connected");
    } catch (e) {
      toast.error("Could not connect", { description: String(e) });
    }
  }, []);

  const deleteSelected = useCallback(async () => {
    const ids = [...useGraph.getState().multiSelected];
    if (!ids.length) return;
    for (const id of ids) {
      const node = useGraph.getState().nodes.get(id);
      const docId = node?.type === "document" ? node.document_id : null;
      try {
        // Documents get a full delete (leave the atlas + free re-upload);
        // other nodes just delete that node.
        await api.request(
          "DELETE",
          docId ? `/api/documents/${docId}` : `/api/nodes/${id}`,
        );
        useGraph.getState().removeNode(id);
        useTabs.getState().closeForNode(id, docId);
      } catch {
        /* skip */
      }
    }
    useGraph.getState().clearMultiSelected();
    void queryClient.invalidateQueries({ queryKey: keys.overview });
    void queryClient.invalidateQueries({ queryKey: keys.documents });
    // Pins on the deleted nodes were removed server-side — refresh the panels.
    void queryClient.invalidateQueries({ queryKey: keys.workspace });
    void queryClient.invalidateQueries({ queryKey: keys.projects });
    // Removing nodes drops their edges too — the orbit layout is now stale.
    void queryClient.invalidateQueries({ queryKey: keys.orbits });
    toast.success(`Removed ${ids.length} node(s)`);
  }, []);

  const saveSelectionAsProject = useCallback(async () => {
    const ids = [...useGraph.getState().multiSelected];
    if (!ids.length) return;
    try {
      await api.request("POST", "/api/projects", {
        name: `Project · ${ids.length} node${ids.length > 1 ? "s" : ""}`,
        node_ids: ids,
      });
      void queryClient.invalidateQueries({ queryKey: keys.projects });
      void queryClient.invalidateQueries({ queryKey: keys.workspace });
      toast.success("Saved as a project");
    } catch (e) {
      toast.error("Could not save project", { description: String(e) });
    }
  }, []);

  const connectSelected = useCallback(async () => {
    const ids = [...useGraph.getState().multiSelected];
    if (ids.length < 2) {
      toast.info("Select at least two nodes to connect");
      return;
    }
    let made = 0;
    for (let i = 1; i < ids.length; i++) {
      try {
        const edge = await api.request<import("@/lib/api").EdgeOut>("POST", "/api/edges", {
          src_id: ids[0],
          dst_id: ids[i],
          type: "similar_to",
        });
        useGraph.getState().mergeBatch([], [edge]);
        made++;
      } catch {
        /* ontology / dup — skip */
      }
    }
    // New connections invalidate the orbit layout — light the dirty banner.
    void queryClient.invalidateQueries({ queryKey: keys.orbits });
    toast.success(`Linked ${made} connection(s)`);
  }, []);

  // Per-frame flash animation runs only while a flash is live AND the device
  // isn't a coarse pointer drowning in nodes; otherwise paintNode gets `now: 0`
  // (its "don't animate" sentinel) and does no time-based work each frame.
  const heavyEffectsOn = !(coarsePointer && data.nodes.length > 400);
  const liveNow = flashActive && heavyEffectsOn;

  // Pause the simulation + render loop while the tab is hidden.
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    if (visible) fg.resumeAnimation();
    else fg.pauseAnimation();
  }, [visible, size.width]);

  /** The most labels the canvas will draw in one frame.
   *
   *  Hovering a hub used to paint one per neighbour — 31 on a 30-edge node. The
   *  neighbours live in the rail now; this is a hard ceiling on what can reach
   *  the canvas even so, so a dense cluster can never become a wall of text. */
  const LABEL_BUDGET = 12;

/** How long the pointer must rest on a node before its card opens.
 *
 * Long enough that crossing a cluster costs nothing, short enough that it never
 * feels like waiting. Leaving is not delayed at all — asymmetry is right here,
 * because appearing is a guess about intent and disappearing is a fact. */
const HOVER_CARD_DELAY_MS = 140;

  const paintCtx = useMemo(
    () => ({
      selectedNodeId: selection?.kind === "node" ? selection.id : null,
      selectedEdgeId: selection?.kind === "edge" ? selection.id : null,
      hoverNodeId,
      hoverLinkId,
      neighborIds,
      pinnedIds,
      multiSelected,
      flash,
      now: 0, // overridden per-frame in nodeCanvasObject; unused by links/labels
      // Reset at the top of every frame — see the nodeCanvasObject below.
      labelRects: [],
      labelBudget: 0,
    }),
    [selection, hoverNodeId, hoverLinkId, neighborIds, pinnedIds, multiSelected, flash],
  );

  useEffect(() => () => window.clearTimeout(cardTimer.current), []);

  const handleExpand = useCallback(
    async (nodeId: string, opts?: { quiet?: boolean }) => {
      const { expanded, mergeNeighborhood } = useGraph.getState();
      if (expanded.has(nodeId)) return;
      setExpanding(nodeId);
      try {
        mergeNeighborhood(await expand(nodeId));
      } catch (err) {
        // `quiet` = best-effort callers (the focus flow, which may hold a
        // DOCUMENT id that legitimately isn't expandable) — no error surface.
        if (!opts?.quiet) {
          toast.error("Could not expand node", {
            description: err instanceof Error ? err.message : String(err),
          });
        }
      } finally {
        setExpanding(null);
      }
    },
    [expand],
  );

  // Citation / palette / post-ingest focus requests: load if needed, then
  // glide to it. Polls for placement so it works even when the graph tab was
  // JUST activated (canvas mounting + first simulation tick takes a moment).
  useEffect(() => {
    if (!focusRequest) return;
    const { nodeId } = focusRequest;
    let cancelled = false;
    // Focus requests arrive with either a graph NODE id or (from the explorer /
    // upload flows) a DOCUMENT id — resolve to the graph node that represents it
    // (matching `document_id`) before expanding/centering, re-resolving each
    // poll tick since the overview merge may land the node mid-poll. Expanding
    // an unresolved id is best-effort and QUIET: a doc-id would 404 on
    // /graph/expand (node ids only), which is expected, not an error.
    const resolve = (requested: string): string | null => {
      const { nodes } = useGraph.getState();
      if (nodes.get(requested)) return requested;
      for (const n of nodes.values()) {
        if (n.document_id === requested) return n.id;
      }
      return null;
    };
    const go = async () => {
      const known = resolve(nodeId);
      if (!known) await handleExpand(nodeId, { quiet: true });
      let tries = 0;
      const tick = () => {
        if (cancelled) return;
        const id = resolve(nodeId);
        const placed = id ? useGraph.getState().nodes.get(id) : undefined;
        const fg = fgRef.current;
        if (fg && id && placed?.x !== undefined && placed?.y !== undefined) {
          fg.centerAt(placed.x, placed.y, 700);
          fg.zoom(2.6, 700);
          useGraph.getState().markFlash([id]);
          useGraph.getState().select({ kind: "node", id });
          useGraph.getState().clearFocus();
          return;
        }
        if (tries++ < 30) {
          setTimeout(tick, 90); // up to ~2.7s for the canvas to settle
        } else {
          useGraph.getState().clearFocus();
        }
      };
      tick();
    };
    void go();
    return () => {
      cancelled = true;
    };
  }, [focusRequest, handleExpand]);

  const lastClickRef = useRef<{ id: string; ts: number }>({ id: "", ts: 0 });
  const onNodeClick = useCallback(
    (node: GNode) => {
      // Form-edge mode: first click picks the source, second click draws the edge.
      if (formEdge) {
        if (!formSource) {
          setFormSource(node.id);
          useGraph.getState().select({ kind: "node", id: node.id });
        } else if (formSource !== node.id) {
          // Do NOT write yet. Ask what the connection means first — the picker
          // is anchored at the target so the question lands where the eye is.
          const fg = fgRef.current;
          const pt = fg?.graph2ScreenCoords?.(node.x ?? 0, node.y ?? 0);
          setEdgeDraft({
            srcId: formSource,
            dstId: node.id,
            x: Math.min(Math.max(8, (pt?.x ?? 0) + 14), Math.max(8, size.width - 244)),
            y: Math.min(Math.max(8, (pt?.y ?? 0) + 10), Math.max(8, size.height - 200)),
          });
          setFormSource(null);
          setFormEdge(false);
        }
        return;
      }
      const now = Date.now();
      const isDouble = lastClickRef.current.id === node.id && now - lastClickRef.current.ts < 320;
      lastClickRef.current = { id: node.id, ts: now };
      // A double-click on a document opens its reader and keeps it selected.
      if (isDouble && node.type === "document" && node.document_id) {
        useGraph.getState().select({ kind: "node", id: node.id });
        openReader(node.document_id, node.label);
        return;
      }
      // Single click selects for the inspector; clicking the already-selected
      // node clears it (toggle). Concepts are revealed only via the inspector's
      // "Concept branch" — the atlas stays files-only by default.
      useGraph.getState().toggleSelect({ kind: "node", id: node.id });
    },
    [openReader, formEdge, formSource, size.width, size.height],
  );

  const openCluster = useCallback(
    async (clusterId: string, label: string) => {
      try {
        const detail = await clusterDetail(clusterId);
        const fresh = useGraph.getState().mergeBatch(detail.nodes, detail.edges);
        useGraph.getState().markFlash(fresh.length ? fresh : detail.nodes.map((n) => n.id));
        toast(`Opened “${label}”`, { description: `${detail.nodes.length} nodes in view.` });
        // Collapse the chip list now that a concept map is in view (re-open via
        // the "Concepts" toggle).
        setClustersManual(false);
        setTimeout(() => fgRef.current?.zoomToFit(600, 90), 500);
      } catch (err) {
        toast.error("Could not open cluster", {
          description: err instanceof Error ? err.message : String(err),
        });
      }
    },
    [clusterDetail],
  );

  // Show the onboarding takeover ONLY when the atlas is truly empty: the user
  // has no content of any kind (not just no documents) AND nothing is drawn on
  // the canvas — so org hubs / annotations that outlive the last document are
  // never hidden behind "Begin your atlas."
  // The card's node, not the hovered one: the two differ for the delay window,
  // and for the frame after the pointer leaves.
  const cardNode = card && card.id === hoverNodeId ? (nodesMap.get(card.id) ?? null) : null;

  const isEmpty =
    overview.isSuccess && !atlasHasContent(overview.data) && nodesMap.size === 0;

  const clusters = overview.data?.clusters ?? [];
  // Manual toggle overrides; otherwise open on desktop, collapsed on compact.
  const clustersOpen = clustersManual ?? !compact;

  return (
    <div
      ref={boxRef}
      className={cn("relative h-full w-full overflow-hidden bg-background", !visible && "hidden")}
    >
      {/* Canvas */}
      {size.width > 0 && (
        <ForceGraph2D<GNode, GLink>
          key={dpr}
          ref={fgRef}
          width={size.width}
          height={size.height}
          graphData={data}
          backgroundColor="rgba(0,0,0,0)"
          nodeId="id"
          nodeRelSize={1}
          nodeVal={(n) => nodeRadius(n) ** 2}
          nodeLabel={() => ""}
          nodeCanvasObject={(node, ctx, scale) => {
            // force-graph gives no per-frame callback, so the FIRST node of each
            // frame resets the label budget. `data.nodes[0]` is stable within a
            // frame and re-entered on the next one, which is all this needs.
            if (node === data.nodes[0]) {
              paintCtx.labelRects.length = 0;
              paintCtx.labelBudget = LABEL_BUDGET;
            }
            // Mutate the shared frame context in place rather than allocating a
            // fresh `{ ...paintCtx }` per node per frame — at 60fps × N nodes
            // that spread was steady minor-GC churn. `now` is only read by
            // paintNode (links/labels ignore it), so reusing the object is safe.
            paintCtx.now = liveNow ? Date.now() : 0;
            paintNode(node, ctx, scale, paintCtx);
          }}
          nodePointerAreaPaint={(node, color, ctx) => {
            ctx.beginPath();
            ctx.arc(node.x ?? 0, node.y ?? 0, nodeRadius(node) + 4, 0, 2 * Math.PI);
            ctx.fillStyle = color;
            ctx.fill();
          }}
          linkColor={(l) => linkColor(l, paintCtx)}
          linkWidth={(l) => linkWidth(l, paintCtx)}
          linkLineDash={(l) => linkDash(l)}
          linkLabel={(l) => linkText(l)}
          linkCanvasObjectMode={() => "after"}
          linkCanvasObject={(l, ctx, scale) => paintLinkLabel(l, ctx, scale, paintCtx)}
          onNodeClick={onNodeClick}
          onNodeHover={(n) => {
            useGraph.getState().setHover(n?.id ?? null);
            window.clearTimeout(cardTimer.current);
            if (!n) {
              // Leaving is instant: a card that lingers after the pointer has
              // gone is a card in the way.
              setCard(null);
              return;
            }
            const { x, y } = pointer.current;
            cardTimer.current = window.setTimeout(
              () => setCard({ id: n.id, x, y }),
              HOVER_CARD_DELAY_MS,
            );
          }}
          onLinkHover={(l) => setHoverLinkId(l?.id ?? null)}
          onLinkClick={(l) => useGraph.getState().toggleSelect({ kind: "edge", id: l.id })}
          onBackgroundClick={(event: MouseEvent) => {
            useGraph.getState().select(null);
            setQuickAdd(null);
            if (formEdge || shiftHeld) return;
            // Double-click on empty sky (two clicks, close in time and space)
            // opens the quick-add menu at the cursor.
            const box = boxRef.current?.getBoundingClientRect();
            const x = event.clientX - (box?.left ?? 0);
            const y = event.clientY - (box?.top ?? 0);
            const last = lastBgClick.current;
            const now = performance.now();
            if (last && now - last.t < 420 && Math.hypot(x - last.x, y - last.y) < 14) {
              lastBgClick.current = null;
              setQuickAdd({
                x: Math.min(x, (box?.width ?? x) - 180),
                y: Math.min(y, (box?.height ?? y) - 150),
              });
            } else {
              lastBgClick.current = { t: now, x, y };
            }
          }}
          onEngineStop={() => {
            if (!didFitRef.current && data.nodes.length > 0) {
              didFitRef.current = true;
              fgRef.current?.zoomToFit(500, 100);
              // A sparse atlas (1–3 nodes) would otherwise zoom to the moon.
              setTimeout(() => {
                const fg = fgRef.current;
                if (fg && fg.zoom() > 2.2) fg.zoom(2.2, 300);
              }, 550);
            }
          }}
          cooldownTicks={120}
          minZoom={0.3}
          maxZoom={9}
        />
      )}

      {/* The hover answer. Suppressed while a gesture is running — during
          form-edge, a rubber-band drag or a quick-add the pointer is being
          used to DO something, and a card following it is in the way. */}
      {cardNode && card && !formEdge && !shiftHeld && !quickAdd && !edgeDraft && (
        <NodeHoverCard node={cardNode} x={card.x} y={card.y} bounds={size} />
      )}

      {/* Cluster chips + edit toolbar */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-start justify-between gap-2 p-3 sm:gap-4">
        {clusters.length > 0 ? (
          <div className="pointer-events-auto flex max-w-[64%] flex-col items-start gap-1.5 sm:max-w-[60%]">
            <Tip label={clustersOpen ? "Hide concept clusters" : "Show concept clusters"}>
              <button
                onClick={() => setClustersManual(!clustersOpen)}
                aria-expanded={clustersOpen}
                className="flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-popover/85 px-2.5 py-1 font-mono text-2xs text-muted-foreground backdrop-blur transition-colors hover:border-primary/50 hover:text-foreground"
              >
                <Orbit className="h-3 w-3 text-primary/70" />
                Concepts
                <span className="text-muted-foreground/70">{clusters.length}</span>
                <ChevronDown className={cn("h-3 w-3 transition-transform", !clustersOpen && "-rotate-90")} />
              </button>
            </Tip>
            {clustersOpen && (
              <div className="flex max-h-[40vh] w-full flex-wrap gap-1.5 overflow-y-auto overscroll-contain pr-0.5 max-sm:max-h-[28vh]">
                {clusters.map((c) => {
                  const chip = (
                    <button
                      onClick={() => void openCluster(c.id, c.label)}
                      className="group flex h-fit items-center gap-1.5 rounded-full border border-border bg-popover/85 px-2.5 py-1 font-mono text-2xs text-muted-foreground backdrop-blur transition-colors hover:border-primary/50 hover:text-foreground"
                    >
                      <Orbit className="h-3 w-3 text-primary/70 group-hover:text-primary" />
                      {c.label}
                      <span className="text-muted-foreground/70">{c.size}</span>
                    </button>
                  );
                  return c.summary ? (
                    <Tip key={c.id} label={c.summary}>
                      {chip}
                    </Tip>
                  ) : (
                    <div key={c.id}>{chip}</div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div />
        )}
        {/* Wraps rather than overflowing: at phone widths the four controls
            don't fit one row beside the cluster chips, and a non-wrapping row
            pushed "Org node"/"Form edge" off the right edge — unreachable. At
            desktop widths the row still fits on one line, so nothing moves. */}
        <div className="pointer-events-auto flex flex-wrap items-center justify-end gap-1.5">
          <GraphLayers />
          <Tip label="Open the orbit view — nodes seated on circular tracks">
            <Button
              size="sm"
              variant="outline"
              onClick={() => useTabs.getState().open({ id: "orbits", kind: "orbits", title: "orbits" })}
            >
              <Orbit className="h-3.5 w-3.5 text-primary/80" />
              Orbits
            </Button>
          </Tip>
          <Tip label="Create an organizational node — a green square that gathers related material">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOrgDialog(true)}
              style={{ color: NODE_COLOR.org }}
              className="hover:brightness-110"
            >
              <SquarePlus className="h-3.5 w-3.5" />
              Org node
            </Button>
          </Tip>
          <Tip label="Draw an edge: click a source node, then a target">
            <Button
              size="sm"
              variant={formEdge ? "default" : "outline"}
              onClick={() => {
                setFormEdge((v) => !v);
                setFormSource(null);
              }}
            >
              <Link2 className="h-3.5 w-3.5" />
              {formEdge ? (formSource ? "Click a target" : "Click a source") : "Form edge"}
            </Button>
          </Tip>
          <Tip label="Fullscreen — the atlas fills the screen (Esc exits)">
            <Button
              size="sm"
              variant="outline"
              aria-label="Enter fullscreen"
              onClick={() => useShell.getState().toggleFullscreen()}
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
          </Tip>
        </div>
      </div>

      <OrgNodeDialog open={orgDialog} onOpenChange={setOrgDialog} />

      {/* Double-click quick-add menu */}
      {quickAdd && (
        <div
          className="absolute z-30 w-[176px] rounded-md border border-border bg-popover/95 p-1 shadow-deep backdrop-blur animate-rise-in"
          style={{ left: quickAdd.x, top: quickAdd.y }}
        >
          <p className="px-2 pb-1 pt-0.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
            Add to the atlas
          </p>
          <QuickItem
            icon={<FileUp className="h-3.5 w-3.5 text-primary/80" />}
            label="Add file…"
            onClick={() => {
              setQuickAdd(null);
              fileInputRef.current?.click();
            }}
          />
          <QuickItem
            icon={<Globe2 className="h-3.5 w-3.5 text-skyb/90" />}
            label="Add website…"
            onClick={() => {
              setQuickAdd(null);
              onAddWebsite();
            }}
          />
          <QuickItem
            icon={<NotebookPen className="h-3.5 w-3.5 text-primary/80" />}
            label="New handwritten note…"
            onClick={() => {
              setQuickAdd(null);
              useShell.getState().requestCreate("note");
            }}
          />
          <QuickItem
            icon={<FilePen className="h-3.5 w-3.5 text-primary/80" />}
            label="New markdown note…"
            onClick={() => {
              setQuickAdd(null);
              void createMarkdownNote();
            }}
          />
          <QuickItem
            icon={<SquarePlus className="h-3.5 w-3.5" style={{ color: NODE_COLOR.org }} />}
            label="New org node…"
            onClick={() => {
              setQuickAdd(null);
              setOrgDialog(true);
            }}
          />
          <QuickItem
            icon={<MessageCircleQuestion className="h-3.5 w-3.5 text-muted-foreground" />}
            label="Ask the atlas"
            onClick={() => {
              setQuickAdd(null);
              useTabs.getState().openAsk();
            }}
          />
        </div>
      )}

      {/* Hidden picker behind the quick-add "Add file…" item. */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={uploadAccept()}
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) void upload(e.target.files);
          e.target.value = "";
        }}
      />

      {/* What does this connection MEAN? Asked once, at the moment the second
          node is clicked, from the types the server says are legal for this
          pair — so the picker can never offer an edge the chokepoint refuses. */}
      {edgeDraft && (
        <div
          className="absolute z-30 w-[228px] rounded-md border border-border bg-popover/95 p-1 shadow-deep backdrop-blur animate-rise-in"
          style={{ left: edgeDraft.x, top: edgeDraft.y }}
        >
          <p className="px-2 pb-1 pt-1 text-[11.5px] leading-snug text-muted-foreground">
            <span className="text-foreground/90">
              {nodesMap.get(edgeDraft.srcId)?.label ?? "source"}
            </span>
            {" … "}
            <span className="text-foreground/90">
              {nodesMap.get(edgeDraft.dstId)?.label ?? "target"}
            </span>
          </p>
          {(() => {
            const src = nodesMap.get(edgeDraft.srcId);
            const dst = nodesMap.get(edgeDraft.dstId);
            const options =
              src && dst ? authorableTypes(ontology.data?.edge_types, src.type, dst.type) : [];
            if (!options.length) {
              if (ontology.isLoading) {
                return (
                  <p className="px-2 pb-2 pt-1 text-[11.5px] text-muted-foreground">
                    Loading relationships…
                  </p>
                );
              }
              // The matrix did not arrive. Do NOT refuse the connection over
              // it: `similar_to` is (ANY, ANY) in the ontology, so it is legal
              // between any two nodes and the server will accept it. Failing
              // closed here would mean an offline blip takes away the one
              // gesture the atlas is for.
              if (ontology.isError) {
                return (
                  <>
                    <p className="px-2 pb-1 pt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                      Could not load the relationship list. You can still make a
                      plain connection.
                    </p>
                    <button
                      onClick={() => {
                        void createUserEdge(edgeDraft.srcId, edgeDraft.dstId, "similar_to");
                        setEdgeDraft(null);
                      }}
                      className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12.5px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    >
                      <span
                        className="h-0.5 w-4 shrink-0 rounded-full"
                        style={{ background: EDGE_COLOR.similar_to }}
                      />
                      is similar to
                    </button>
                  </>
                );
              }
              return (
                <p className="px-2 pb-2 pt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                  No relationship is allowed between these two.
                </p>
              );
            }
            return options.map((rule) => (
              <button
                key={rule.type}
                onClick={() => {
                  void createUserEdge(edgeDraft.srcId, edgeDraft.dstId, rule.type);
                  setEdgeDraft(null);
                }}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12.5px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <span
                  className="h-0.5 w-4 shrink-0 rounded-full"
                  style={{ background: EDGE_COLOR[rule.type] ?? "#55555e" }}
                />
                {rule.phrase}
              </button>
            ));
          })()}
          <button
            onClick={() => setEdgeDraft(null)}
            className="mt-0.5 w-full rounded px-2 py-1 text-left text-[11.5px] text-muted-foreground/70 transition-colors hover:bg-accent hover:text-foreground"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Hint strip while drawing an edge or selecting */}
      {(formEdge || shiftHeld) && (
        <div className="pointer-events-none absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-full border border-primary/30 bg-popover/90 px-3 py-1 font-mono text-2xs text-primary backdrop-blur">
          {formEdge ? "form edge — click source then target (Esc to cancel)" : "shift-click or shift-drag to select multiple"}
        </div>
      )}

      {/* Rubber-band multi-select: only intercepts while Shift is held. */}
      {shiftHeld && visible && (
        <div
          className="absolute inset-0 z-20 cursor-crosshair"
          onMouseDown={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - r.left;
            const y = e.clientY - r.top;
            setBand({ x0: x, y0: y, x1: x, y1: y });
          }}
          onMouseMove={(e) => {
            if (!band) return;
            const r = e.currentTarget.getBoundingClientRect();
            setBand({ ...band, x1: e.clientX - r.left, y1: e.clientY - r.top });
          }}
          onMouseUp={() => {
            const fg = fgRef.current;
            if (band && fg) {
              const sel = new Set(useGraph.getState().multiSelected);
              const dragDist = Math.hypot(band.x1 - band.x0, band.y1 - band.y0);
              const zoom = fg.zoom();
              if (dragDist < 5) {
                // A shift-CLICK toggles the single node under the cursor.
                let hit: GNode | null = null;
                let hitD = Infinity;
                for (const n of nodesMap.values()) {
                  if (n.x == null || n.y == null) continue;
                  const s = fg.graph2ScreenCoords(n.x, n.y);
                  const d = Math.hypot(s.x - band.x1, s.y - band.y1);
                  if (d < hitD) {
                    hitD = d;
                    hit = n;
                  }
                }
                if (hit && hitD <= nodeRadius(hit) * zoom + 6) {
                  if (sel.has(hit.id)) sel.delete(hit.id);
                  else sel.add(hit.id);
                }
              } else {
                // A shift-DRAG adds every node inside the band to the selection.
                const minX = Math.min(band.x0, band.x1);
                const maxX = Math.max(band.x0, band.x1);
                const minY = Math.min(band.y0, band.y1);
                const maxY = Math.max(band.y0, band.y1);
                for (const n of nodesMap.values()) {
                  if (n.x == null || n.y == null) continue;
                  const s = fg.graph2ScreenCoords(n.x, n.y);
                  if (s.x >= minX && s.x <= maxX && s.y >= minY && s.y <= maxY) sel.add(n.id);
                }
              }
              useGraph.getState().setMultiSelected(sel);
            }
            setBand(null);
          }}
        >
          {band && (
            <div
              className="absolute border border-primary/70 bg-primary/10"
              style={{
                left: Math.min(band.x0, band.x1),
                top: Math.min(band.y0, band.y1),
                width: Math.abs(band.x1 - band.x0),
                height: Math.abs(band.y1 - band.y0),
              }}
            />
          )}
        </div>
      )}

      {/* Bulk actions on the multi-selection (raised above the mobile dock) */}
      {multiSelected.size > 0 && (
        <div
          className={cn(
            "absolute left-1/2 z-30 flex max-w-[calc(100vw-1.5rem)] -translate-x-1/2 flex-wrap items-center justify-center gap-1.5 rounded-3xl border border-border bg-popover/95 px-2.5 py-1.5 shadow-deep backdrop-blur",
            compact ? "bottom-[84px]" : "bottom-4",
          )}
        >
          <span className="px-1 font-mono text-2xs text-muted-foreground">{multiSelected.size} selected</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              useTabs.getState().openAsk({
                scopeNodeIds: [...multiSelected],
                title: `ask · ${multiSelected.size} nodes`,
              })
            }
          >
            <MessageCircleQuestion className="h-3.5 w-3.5" /> Ask
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void saveSelectionAsProject()}>
            <FolderPlus className="h-3.5 w-3.5" /> Project
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void connectSelected()}>
            <Link2 className="h-3.5 w-3.5" /> Connect
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className={cn(bulkConfirm ? "bg-destructive/15 text-destructive" : "text-destructive/80 hover:text-destructive")}
            onClick={() => {
              if (bulkConfirm) {
                void deleteSelected();
                setBulkConfirm(false);
              } else {
                setBulkConfirm(true);
              }
            }}
          >
            <Trash2 className="h-3.5 w-3.5" /> {bulkConfirm ? `Delete ${multiSelected.size}?` : "Delete"}
          </Button>
          <Tip label="Clear selection">
            <button
              onClick={() => useGraph.getState().clearMultiSelected()}
              className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </Tip>
        </div>
      )}

      {/* Expanding indicator */}
      {expanding && (
        <div
          className={cn(
            "absolute left-3 z-10 flex items-center gap-2 rounded-full border border-border bg-popover/90 px-3 py-1.5 font-mono text-2xs text-muted-foreground",
            compact ? "bottom-[84px]" : "bottom-3",
          )}
        >
          <Loader2 className="h-3 w-3 animate-spin text-primary" /> charting neighbors…
        </div>
      )}

      {/* Loading / error / empty states */}
      {overview.isLoading && (
        <div className="absolute inset-0 grid place-items-center">
          <LoadingState label="surveying the atlas…" />
        </div>
      )}
      {/* Signed out in hosted mode (401/403): invite sign-in — NOT a backend error. */}
      {overview.isError && isSignedOut(overview.error) && (
        <div className="absolute inset-0 grid place-items-center">
          <RestrictedState reason="auth" />
        </div>
      )}

      {/* A genuine failure (network, 5xx, local server down): a curated error card. */}
      {overview.isError && !isSignedOut(overview.error) && (
        <div className="absolute inset-0 grid place-items-center">
          <ErrorState error={overview.error} onRetry={() => void overview.refetch()} label="atlas" />
        </div>
      )}
      {isEmpty && (
        <EmptyAtlas
          onPick={(files) => void upload(files)}
          onAddWebsite={onAddWebsite}
        />
      )}
    </div>
  );
}

/** A hosted-mode 401/403 means "not signed in" — show the sign-in card, not the
 * scary backend-down hint (which is for a real network/5xx outage). Duck-type
 * the status field instead of `instanceof GraphMindApiError`, so a second copy
 * of the client class slipping into the bundle can't misroute a 401 to the
 * backend-down screen. */
function isSignedOut(error: unknown): boolean {
  const status = (error as { status?: number } | null | undefined)?.status;
  return status === 401 || status === 403;
}

/** The empty atlas: the shared onboarding EmptyState, its `action` wired to
 * the same "open the file picker" affordance the old bespoke hero used —
 * plus the "paste a website" affordance kept alongside it (EmptyState has
 * room for exactly one action; the second stays a plain link underneath). */
function EmptyAtlas({
  onPick,
  onAddWebsite,
}: {
  onPick: (files: FileList) => void;
  onAddWebsite: () => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    // The overlay must not swallow canvas clicks (double-click-on-empty-sky
    // opens the quick-add menu): the outer, full-canvas wrapper is
    // pointer-events-none, and ONLY the actual interactive affordances — the
    // EmptyState action button + the "paste a website link" button — opt back
    // in via the `[&_button]:pointer-events-auto` scoped re-enable. Text/icon
    // chrome in between stays click-through.
    <div className="pointer-events-none absolute inset-0 grid place-items-center">
      <div className="flex flex-col items-center gap-3 [&_button]:pointer-events-auto">
        <EmptyState
          icon={FileUp}
          title="Begin your atlas"
          description="Drop a PDF, markdown, text file or website link anywhere. GraphMind reads its structure, files the source in your vault, and charts it among everything you know."
          action={{ label: "Drop or choose a file…", onClick: () => inputRef.current?.click() }}
        />
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={uploadAccept()}
          className="hidden"
          onChange={(e) => e.target.files && onPick(e.target.files)}
        />
        <button
          type="button"
          onClick={onAddWebsite}
          className="flex items-center gap-1.5 font-mono text-2xs uppercase tracking-widest text-muted-foreground hover:text-primary"
        >
          <Link2 className="h-3.5 w-3.5" /> or paste a website link
        </button>
      </div>
    </div>
  );
}
